import React from 'react';
import { RocketLaunchIcon, WrenchScrewdriverIcon, ChatBubbleLeftRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';

export default function Stage2() {
  return (
    <div className="space-y-8">
      <PlaybookSection
        title="Stage 2: Fix The Thing"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading="Implement solutions and maintain communication"
      >
        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">TL;DR</h2>
          <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
            <p className="mb-4">You need to do 2 things:</p>
            <ul className="list-none space-y-3">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">1.</span>
                <span>Fix The Thing</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">2.</span>
                <span>Keep the team in the loop on what you are actioning and invite feedback/support where appropriate</span>
              </li>
            </ul>
            <p className="mt-4">Here's what we mean…</p>
          </ExpandableCallout>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Fix The Thing</h2>
          <p className="mb-4">Whatever your context from the previous step, there is going to be some action or intervention that's required to make things right.</p>
          <p className="mb-4">Now is the time to do that.</p>
          <ul className="space-y-2 text-gray-300">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Payroll screw up? Now we make sure everyone gets paid</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Poor executive hire? Now we off-board and listen to the impacted teams to understand what they needed</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Strategy failure caused lay-offs? Now we manage the restructure</span>
            </li>
          </ul>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Keep Folks In The Loop And Invite Feedback/Support Where Appropriate</h2>
          <p className="mb-4">Communication is key during recovery. Here's how we keep everyone in the loop and make this a collective effort</p>
          
          <h3 className="text-lg font-semibold text-white mb-3">Foundations:</h3>
          <p className="mb-4 text-gray-300 italic">When the solution is obvious or quick to implement</p>
          
          <ul className="space-y-2 text-gray-300 mb-6">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span><strong>Regular Updates</strong>: Set up a schedule for regular updates. This could be daily or weekly emails, a dedicated Slack channel <img src='https://auth.openorg.fyi/storage/v1/object/public/web-assets//slack-new-logo.svg' alt='Slack logo' className='inline h-5 w-5 ml-1 align-text-bottom' />, or quick stand-up meetings to discuss the progress of the fixes being implemented.</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span><strong>Open Door for Feedback</strong>: Encourage open dialogue. Make it clear that feedback isn't just welcome, it's wanted. Set up anonymous feedback tools if necessary to ensure everyone feels comfortable being honest.</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span><strong>Acknowledge Contributions</strong>: As you make progress, acknowledge the contributions of the team. Lots of kudos, shoutouts and emoji love.</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span><strong>Transparent Progress</strong>: Use visual tools like miro boards, dashboards or project management tech to track the progress of the fixes being implemented. This transparency helps build trust and keeps everyone informed about what's happening and why.</span>
            </li>
          </ul>

          <h3 className="text-lg font-semibold text-white mb-3">Add-ons</h3>
          <p className="mb-2 text-gray-300 italic">When the solution is complex or needs to emerge</p>
          
          <ul className="space-y-2 text-gray-300 mb-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span><strong>Crowd-source</strong>: Don't just fix things from the top down. Invite suggestions and support from the team.</span>
            </li>
          </ul>

          <ExpandableCallout type="tip" title="Appreciate this stage is going to be contextual to whatever the f*ck up you are managing is so…." defaultOpen={true} className="mt-4">
            <p>Each situation will require its own unique approach and timeline.</p>
          </ExpandableCallout>
        </PlaybookCard>
      </PlaybookSection>
    </div>
  );
} 